/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './MdmMatchingSteps.module.scss';

export const MdmMatchingSteps = () => {
  const { t } = useInt();
  const railRef = useRef<HTMLDivElement>(null);
  const hasIntersected = useIntersection(railRef, { threshold: 0.15 });

  const steps = [
    {
      num: '01',
      title: t('Normalise per jurisdiction'),
      desc: (
        <>
          {t(
            'Legal-form suffixes, punctuation, diacritics, transliteration, and address formats are normalised using rules specific to the country — because',
          )}{' '}
          <strong>{t('"Ltd", "GmbH", and "S.A."')}</strong>{' '}
          {t('carry different meanings and different collision risks.')}
        </>
      ),
    },
    {
      num: '02',
      title: t('Generate candidates'),
      desc: (
        <>
          {t(
            'The normalised record is blocked against 600M+ registry profiles on name, address, domain, officer names, and any identifier you supplied. Supplying',
          )}{' '}
          <strong>{t('a registration number or postcode')}</strong>{' '}
          {t('is what moves a record from the review band into auto-merge.')}
        </>
      ),
    },
    {
      num: '03',
      title: t('Score on evidence'),
      desc: (
        <>
          {t(
            'Each candidate is scored attribute by attribute, and the agreeing attributes are returned alongside the number. A score of 0.93 arrives with',
          )}{' '}
          <strong>{t('the reason it is 0.93')}</strong> {t('— and with the runner-up it was chosen over.')}
        </>
      ),
    },
    {
      num: '04',
      title: t('You set the merge rules'),
      desc: (
        <>
          {t(
            'Above your auto-merge line, records merge and write back. Below it, they queue for a steward with the evidence already attached. Decisions are retained, so',
          )}{' '}
          <strong>{t("the same ambiguity isn't reviewed twice")}</strong> {t('at the next refresh.')}
        </>
      ),
    },
  ];

  return (
    <Section className={styles.root}>
      <div className={styles.glow} aria-hidden="true" />

      <div className={cn(styles.head, hasIntersected && styles.headIn)}>
        <TitleBlock
          title={t('Four steps, each one inspectable')}
          description={t(
            'Matching is a pipeline rather than a single score. Every stage exposes what it did, so a low-confidence result tells a steward what was missing instead of leaving them to guess.',
          )}
          align="left"
        />
      </div>

      <div ref={railRef} className={cn(styles.rail, hasIntersected && styles.railIn)}>
        <span className={styles.pulse} aria-hidden="true" />

        {steps.map((step, i) => (
          <div key={step.num} className={styles.step} style={{ '--delay': `${i * 0.18}s` } as CSSProperties}>
            <div className={styles.node}>{step.num}</div>
            <div className={styles.body}>
              <h3 className={styles.stepTitle}>{step.title}</h3>
              <p className={styles.stepDesc}>{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};
